import { useEffect, type Dispatch, type SetStateAction } from "react";
import Button from "../../../shared/components/Button";
import InfoTooltip from "../../../shared/components/InfoTooltip";
import OrganizerDatePicker from "../../../shared/components/OrganizerDatePicker";
import OrganizerTimeInput from "../../../shared/components/OrganizerTimeInput";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import {
  buildScheduleDayWindows,
  calendarDaysInclusive,
  dateOnlyFromLocal,
  toDateTimeLocal,
  type ScheduleDraft,
} from "../utils/tournamentOperations";

type AdminTournamentScheduleSettingsPanelProps = {
  eventEndAt?: string | null;
  eventStartAt?: string | null;
  loadingSettings: boolean;
  scheduleDraft: ScheduleDraft;
  section?: "all" | "courts" | "days";
  setScheduleDraft: Dispatch<SetStateAction<ScheduleDraft>>;
};

function courtNamesFromDraft(value: string) {
  return String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function setCourtNames(names: string[]) {
  return names.map((item) => item.trim()).filter(Boolean).join("\n");
}

export function AdminTournamentScheduleSettingsPanel({
  eventEndAt,
  eventStartAt,
  loadingSettings,
  scheduleDraft,
  section = "all",
  setScheduleDraft,
}: AdminTournamentScheduleSettingsPanelProps) {
  const info = siteTexts.common.info;
  const courtNames = courtNamesFromDraft(scheduleDraft.courtNames);
  const breakPeriods = scheduleDraft.breakPeriods || [];
  const daySchedules =
    scheduleDraft.daySchedules && scheduleDraft.daySchedules.length > 0
      ? scheduleDraft.daySchedules
      : buildScheduleDayWindows({
          daysCount: Number(scheduleDraft.daysCount || 1),
          firstDay: dateOnlyFromLocal(scheduleDraft.dayStartAt),
          fallbackEndTime: scheduleDraft.dailyEndTime || "18:00",
          fallbackStartTime: scheduleDraft.dailyStartTime || "08:00",
        });

  useEffect(() => {
    const startLocal = toDateTimeLocal(eventStartAt || "");
    if (!startLocal) return;
    const endLocal = toDateTimeLocal(eventEndAt || eventStartAt || "");
    const nextDaysCount = String(calendarDaysInclusive(startLocal, endLocal));
    const nextFirstDay = dateOnlyFromLocal(startLocal);
    setScheduleDraft((current) => {
      const currentFirstDay = dateOnlyFromLocal(current.dayStartAt);
      if (
        currentFirstDay === nextFirstDay &&
        String(current.daysCount || "1") === nextDaysCount
      ) {
        return current;
      }
      const nextStart = current.dailyStartTime || "08:00";
      const nextEnd = current.dailyEndTime || "18:00";
      return {
        ...current,
        dayStartAt: `${nextFirstDay}T${nextStart}`,
        dayEndAt: endLocal || `${nextFirstDay}T${nextEnd}`,
        daysCount: nextDaysCount,
        daySchedules: buildScheduleDayWindows({
          daysCount: Number(nextDaysCount || 1),
          firstDay: nextFirstDay,
          fallbackEndTime: nextEnd,
          fallbackStartTime: nextStart,
          previous: current.daySchedules,
        }),
      };
    });
  }, [eventEndAt, eventStartAt, setScheduleDraft]);

  return (
    <div className="admin-tournament-ops-panel">
      {loadingSettings ? (
        <Spinner label="Carregando configurações..." />
      ) : (
        <div className="admin-tournament-settings-grid admin-tournament-schedule-grid">
          {(section === "all" || section === "courts") && (
          <fieldset className="admin-tournament-settings-box">
            <legend className="info-label">
              Quadras
              <InfoTooltip content={info.courtsCount} />
            </legend>
            <div className="status-filter-buttons admin-category-tab-actions admin-court-tab-actions">
              {courtNames.map((courtName, index) => (
                <Button
                  key={`court-tab-${index}`}
                  type="button"
                  variant="pill"
                  className="active"
                  onClick={() => undefined}
                >
                  {courtName}
                </Button>
              ))}
              <Button
                type="button"
                variant="pill"
                className="admin-category-add-button"
                aria-label="Adicionar quadra"
                title="Adicionar quadra"
                onClick={() =>
                  setScheduleDraft((current) => {
                    const currentNames = courtNamesFromDraft(current.courtNames);
                    return {
                      ...current,
                      courtNames: setCourtNames([
                        ...currentNames,
                        `Quadra ${currentNames.length + 1}`,
                      ]),
                    };
                  })
                }
              >
                +
              </Button>
            </div>
            <div className="admin-tournament-court-list">
              {courtNames.map((courtName, index) => (
                <div className="admin-tournament-court-row" key={`court-row-${index}`}>
                  <label className="field">
                    <span>Quadra {index + 1}</span>
                    <input
                      type="text"
                      value={courtName}
                      onChange={(event) =>
                        setScheduleDraft((current) => {
                          const next = courtNamesFromDraft(current.courtNames);
                          next[index] = event.target.value;
                          return { ...current, courtNames: setCourtNames(next) };
                        })
                      }
                    />
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={courtNames.length <= 1}
                    onClick={() =>
                      setScheduleDraft((current) => {
                        const next = courtNamesFromDraft(current.courtNames);
                        next.splice(index, 1);
                        return { ...current, courtNames: setCourtNames(next) };
                      })
                    }
                  >
                    Remover
                  </Button>
                </div>
              ))}
            </div>
          </fieldset>
          )}

          {(section === "all" || section === "days") && (
          <fieldset className="admin-tournament-settings-box">
            <legend className="info-label">
              Dias e horários
              <InfoTooltip content={info.groupStage} />
            </legend>
            <div className="admin-tournament-day-list">
              {daySchedules.map((day, index) => (
                <div className="admin-tournament-day-row" key={day.date}>
                  <strong>Dia {index + 1}</strong>
                  <span>{day.date.split("-").reverse().join("/")}</span>
                  <OrganizerTimeInput
                    label={siteTexts.admin.misc.dailyStart}
                    value={day.startTime}
                    onChange={(value) =>
                      setScheduleDraft((current) => {
                        const next = [...daySchedules];
                        next[index] = { ...next[index], startTime: value };
                        return {
                          ...current,
                          dailyStartTime:
                            next[0]?.startTime || current.dailyStartTime,
                          daySchedules: next,
                        };
                      })
                    }
                  />
                  <OrganizerTimeInput
                    label={siteTexts.admin.misc.dailyEnd}
                    value={day.endTime}
                    onChange={(value) =>
                      setScheduleDraft((current) => {
                        const next = [...daySchedules];
                        next[index] = { ...next[index], endTime: value };
                        return {
                          ...current,
                          dailyEndTime:
                            next[next.length - 1]?.endTime ||
                            current.dailyEndTime,
                          daySchedules: next,
                        };
                      })
                    }
                  />
                </div>
              ))}
            </div>
            <div className="admin-tournament-settings-row">
              <label className="field">
                <span>{siteTexts.admin.misc.matchDuration}</span>
                <input
                  type="number"
                  min={10}
                  value={scheduleDraft.defaultMatchDurationMinutes}
                  onChange={(event) =>
                    setScheduleDraft((current) => ({
                      ...current,
                      defaultMatchDurationMinutes: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field">
                <span>Descanso mínimo</span>
                <input
                  type="number"
                  min={0}
                  value={scheduleDraft.minRestMinutes}
                  onChange={(event) =>
                    setScheduleDraft((current) => ({
                      ...current,
                      minRestMinutes: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <div className="admin-tournament-break-list">
              <div className="admin-tournament-break-head">
                <strong>Pausas do cronograma</strong>
                <Button
                  type="button"
                  variant="pill"
                  onClick={() =>
                    setScheduleDraft((current) => {
                      const currentBreaks = current.breakPeriods || [];
                      const firstDay =
                        current.daySchedules?.[0]?.date ||
                        dateOnlyFromLocal(current.dayStartAt);
                      return {
                        ...current,
                        breakPeriods: [
                          ...currentBreaks,
                          {
                            id: `break-${Date.now()}`,
                            label: `Pausa ${currentBreaks.length + 1}`,
                            date: firstDay,
                            startTime: "12:00",
                            endTime: "12:30",
                          },
                        ],
                      };
                    })
                  }
                >
                  Adicionar pausa
                </Button>
              </div>
              {breakPeriods.map((period, index) => (
                <div className="admin-tournament-break-row" key={period.id}>
                  <label className="field">
                    <span>Nome</span>
                    <input
                      type="text"
                      value={period.label}
                      onChange={(event) =>
                        setScheduleDraft((current) => {
                          const next = [...(current.breakPeriods || [])];
                          next[index] = { ...next[index], label: event.target.value };
                          return { ...current, breakPeriods: next };
                        })
                      }
                    />
                  </label>
                  <OrganizerDatePicker
                    label="Data"
                    value={period.date}
                    onChange={(value) =>
                      setScheduleDraft((current) => {
                        const next = [...(current.breakPeriods || [])];
                        next[index] = { ...next[index], date: value };
                        return { ...current, breakPeriods: next };
                      })
                    }
                  />
                  <OrganizerTimeInput
                    label="Início"
                    value={period.startTime}
                    onChange={(value) =>
                      setScheduleDraft((current) => {
                        const next = [...(current.breakPeriods || [])];
                        next[index] = { ...next[index], startTime: value };
                        return { ...current, breakPeriods: next };
                      })
                    }
                  />
                  <OrganizerTimeInput
                    label="Fim"
                    value={period.endTime}
                    onChange={(value) =>
                      setScheduleDraft((current) => {
                        const next = [...(current.breakPeriods || [])];
                        next[index] = { ...next[index], endTime: value };
                        return { ...current, breakPeriods: next };
                      })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      setScheduleDraft((current) => ({
                        ...current,
                        breakPeriods: (current.breakPeriods || []).filter(
                          (_item, itemIndex) => itemIndex !== index,
                        ),
                      }))
                    }
                  >
                    Remover
                  </Button>
                </div>
              ))}
            </div>
          </fieldset>
          )}
        </div>
      )}
    </div>
  );
}
